// Crowd-sourced GPS yardage to green + Caddie club suggestion from the user's bag.
// - If hole has pinned coords → show live distance using navigator.geolocation
//   AND a Caddie suggestion adjusted for current altitude + temperature + goal pace.
// - If not → show "Pin Green" button (when player is standing on the green).
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Flag, Crosshair } from 'lucide-react';
import { Link } from 'react-router-dom';
import { suggestClub } from '@/lib/clubSuggestion';
import { fetchConditions } from '@/lib/conditions';
import { analyseHazards } from '@/lib/hazards';

// Haversine distance in meters
function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Initial compass bearing (degrees) from point 1 toward point 2.
function bearingDeg(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1), φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export default function DistanceToGreen({ courseId, hole, onPinned }) {
  const [pos, setPos] = useState(null);
  const [error, setError] = useState(null);
  const [pinning, setPinning] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [clubsAreDefaults, setClubsAreDefaults] = useState(false);
  const [calibration, setCalibration] = useState({ altitude_ft: 0, temp_f: 70 });
  const [conditions, setConditions] = useState(null);
  // Pin position selector (F/C/B). Defaults to centre. Resets when the user
  // moves to a different hole — pin sheets typically change daily, not hole
  // by hole during a round, but per-hole state keeps the UX simple.
  const [pinPos, setPinPos] = useState('C');
  const watchId = useRef(null);

  // Reset pin position when the active hole changes.
  useEffect(() => { setPinPos('C'); }, [hole?.hole]);

  const pinned = hole?.green_lat && hole?.green_lng;

  // Load the user's bag + calibration once.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/profile/clubs`).then(r => {
      if (cancelled) return;
      setClubs(r.data?.clubs || []);
      setClubsAreDefaults(!!r.data?.seeded);
      if (r.data?.bag_calibration) setCalibration(r.data.bag_calibration);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Pull current conditions for the green's coordinates (cached 30 min).
  useEffect(() => {
    if (!pinned) return;
    let cancelled = false;
    fetchConditions(hole.green_lat, hole.green_lng).then(c => {
      if (!cancelled) setConditions(c);
    });
    return () => { cancelled = true; };
  }, [pinned, hole?.green_lat, hole?.green_lng]);

  useEffect(() => {
    if (!navigator.geolocation) { setError('no-gps'); return; }
    watchId.current = navigator.geolocation.watchPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
      (err) => setError(err.code === 1 ? 'denied' : 'error'),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    return () => {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
    };
  }, []);

  const handlePin = async () => {
    if (!pos) { toast.error('Waiting for GPS…'); return; }
    setPinning(true);
    try {
      await axios.put(`${API}/courses/${courseId}/holes/${hole.hole}/green-pin`,
        { lat: pos.lat, lng: pos.lng, accuracy: pos.acc });
      toast.success('✓ Green pinned — next players will see the distance');
      onPinned && onPinned({ hole_num: hole.hole, lat: pos.lat, lng: pos.lng });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pin failed');
    } finally { setPinning(false); }
  };

  if (error === 'no-gps') {
    return <p className="text-[10px] text-[#6B6E66] text-center">GPS not available</p>;
  }
  if (error === 'denied') {
    return <p className="text-[10px] text-[#6B6E66] text-center">
      Enable location to see distance to green
    </p>;
  }
  if (!pos) {
    return (
      <p className="text-[10px] text-[#6B6E66] text-center flex items-center justify-center gap-1">
        <Crosshair className="h-3 w-3 animate-pulse" /> Locking GPS…
      </p>
    );
  }

  if (!pinned) {
    return (
      <button
        onClick={handlePin}
        disabled={pinning}
        className="w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-[#C96A52]/10 border border-dashed border-[#C96A52]/40 hover:bg-[#C96A52]/20 transition-colors"
        data-testid={`pin-green-${hole.hole}`}>
        <Flag className="h-3.5 w-3.5 text-[#C96A52]" />
        <span className="text-xs font-bold text-[#C96A52]">
          {pinning ? 'Saving…' : 'On the green? Pin it'}
        </span>
      </button>
    );
  }

  // Front / Back yardages when the admin pinned the green's edges. We use
  // these to display a "Front 168y · Center 175y · Back 182y" breakdown — a
  // 12-18y front-back delta is the difference between two clubs.
  const yFront = (hole.green_front_lat != null && hole.green_front_lng != null)
    ? Math.round(distanceMeters(pos.lat, pos.lng, hole.green_front_lat, hole.green_front_lng) * 1.09361)
    : null;
  const yBack = (hole.green_back_lat != null && hole.green_back_lng != null)
    ? Math.round(distanceMeters(pos.lat, pos.lng, hole.green_back_lat, hole.green_back_lng) * 1.09361)
    : null;
  const hasFB = yFront != null && yBack != null;

  // Pin position selector — when F/B are pinned, the player can lock the
  // Caddie's target on the actual flag location. Defaults to centre. The
  // selected edge becomes the basis for both the suggested club's target
  // yardage and the hazard strategy line.
  const targetCoords = pinPos === 'F' && hole.green_front_lat != null
    ? { lat: hole.green_front_lat, lng: hole.green_front_lng }
    : pinPos === 'B' && hole.green_back_lat != null
    ? { lat: hole.green_back_lat, lng: hole.green_back_lng }
    : { lat: hole.green_lat, lng: hole.green_lng };
  const targetMeters = distanceMeters(pos.lat, pos.lng, targetCoords.lat, targetCoords.lng);
  const targetYards = Math.round(targetMeters * 1.09361);
  const targetBearing = bearingDeg(pos.lat, pos.lng, targetCoords.lat, targetCoords.lng);

  const meters = distanceMeters(pos.lat, pos.lng, hole.green_lat, hole.green_lng);
  const yards = Math.round(meters * 1.09361);
  const suggestion = suggestClub(targetYards, clubs, {
    calibration, conditions, shot_bearing: targetBearing,
  });

  // Hazard analysis — uses the chosen pin target so strategy reflects where
  // the player is actually aiming. Passes the suggested club's effective
  // distance so we can filter hazards to ones in the actual landing zone.
  const clubEffective = suggestion?.pick?.effective_yards
    ?? suggestion?.shorter?.effective_yards
    ?? null;
  const { strategy: hazardStrategy } = analyseHazards(
    { lat: pos.lat, lng: pos.lng },
    targetCoords,
    hole.hazards || [],
    { club_yards: clubEffective }
  );

  // Show conditions detail only when something materially changes the shot:
  //   • wind label (≥ 2y impact or any meaningful crosswind), or
  //   • combined altitude+temp adjustment ≥ 5 yards.
  const breakdown = suggestion?.breakdown;
  const altTempAbs = breakdown ? Math.abs((breakdown.altitude_y || 0) + (breakdown.temp_y || 0)) : 0;
  const showHints = breakdown && (breakdown.wind_label || altTempAbs >= 5);
  const breakdownLabel = showHints
    ? [
        // Combine altitude + temp into one short hint when together they shift ≥5y.
        altTempAbs >= 5
          ? (breakdown.altitude_y + breakdown.temp_y > 0
              ? `+${breakdown.altitude_y + breakdown.temp_y}y conditions`
              : `${breakdown.altitude_y + breakdown.temp_y}y conditions`)
          : null,
        breakdown.wind_label || null,
      ].filter(Boolean).join(' · ')
    : '';

  return (
    <div className="space-y-1.5">
      {hasFB ? (
        // When F/C/B are all pinned, render three flag-coloured pill buttons
        // so the player can lock the Caddie target on the actual day's pin.
        <div className="flex items-center gap-1.5" data-testid={`distance-${hole.hole}`}>
          {[
            { k: 'F', y: yFront, bg: '#C53030', label: 'Front' },
            { k: 'C', y: yards,  bg: '#FFFFFF', label: 'Center', textDark: true },
            { k: 'B', y: yBack,  bg: '#1D4ED8', label: 'Back' },
          ].map(({ k, y, bg, label, textDark }) => {
            const active = pinPos === k;
            return (
              <button
                key={k}
                onClick={() => setPinPos(k)}
                className={`flex-1 flex flex-col items-center justify-center py-1.5 rounded-lg transition-all ${
                  active ? 'ring-2 ring-[#1B3C35] scale-[1.02] shadow-sm' : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  backgroundColor: bg,
                  color: textDark ? '#1B3C35' : '#FFFFFF',
                  border: textDark ? '1px solid #E2E3DD' : 'none',
                }}
                data-testid={`pin-pos-${k.toLowerCase()}-${hole.hole}`}
                aria-label={`${label} pin · ${y} yards`}>
                <span className="text-[9px] uppercase tracking-wider font-bold opacity-80">
                  {label}
                </span>
                <span className="text-base font-bold tabular-nums" style={{ fontFamily: 'Outfit' }}>
                  {y}y
                </span>
              </button>
            );
          })}
          <span className="text-[9px] text-[#6B6E66] ml-1">±{Math.round(pos.acc)}m</span>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg bg-[#1B3C35] text-white"
          data-testid={`distance-${hole.hole}`}>
          <Flag className="h-4 w-4 text-[#C96A52]" />
          <span className="text-xs uppercase tracking-wider font-bold opacity-75">To Green</span>
          <span className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Outfit' }}>
            {yards}y
          </span>
          <span className="text-[10px] opacity-60">±{Math.round(pos.acc)}m</span>
        </div>
      )}
      {suggestion && (
        <div className="py-2 px-3 rounded-lg bg-[#F4E9D8]/60 border border-[#C96A52]/20 text-center"
          data-testid={`caddie-${hole.hole}`}>
          {clubsAreDefaults && (
            <Link to="/my-bag"
              className="flex items-center justify-center gap-1 mb-1.5 -mt-0.5 px-2 py-1 rounded-md bg-[#C96A52]/15 hover:bg-[#C96A52]/25 transition-colors text-[10px] font-semibold text-[#C96A52] uppercase tracking-wider"
              data-testid={`default-clubs-banner-${hole.hole}`}>
              ⚠️ Using generic distances — tap to personalize your bag
            </Link>
          )}
          <div className="text-xs text-[#6B6E66] uppercase tracking-wider font-bold mb-1 flex items-center justify-center gap-1">
            <span>🧢</span> Caddie says
          </div>
          {suggestion.mode === 'single' && (
            <div>
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-3xl font-bold text-[#1B3C35]" style={{ fontFamily: 'Outfit' }}>
                  {suggestion.pick.name}
                </span>
                <span className="text-base text-[#6B6E66] tabular-nums">
                  plays {suggestion.pick.effective_yards}y
                </span>
              </div>
              {breakdownLabel && (
                <div className="text-[10px] text-[#6B6E66] mt-0.5">{breakdownLabel}</div>
              )}
            </div>
          )}
          {suggestion.mode === 'range' && (
            <div>
              <div className="flex items-stretch justify-center gap-3 text-center">
                <div className="flex-1">
                  <div className="text-3xl font-bold text-[#1B3C35]" style={{ fontFamily: 'Outfit' }}>
                    {suggestion.shorter.name}
                  </div>
                  <div className="text-sm text-[#6B6E66]">
                    plays {suggestion.shorter.effective_yards}y
                  </div>
                </div>
                <div className="text-[#C96A52] self-center text-base font-bold">or</div>
                <div className="flex-1">
                  <div className="text-3xl font-bold text-[#1B3C35]" style={{ fontFamily: 'Outfit' }}>
                    {suggestion.longer.name}
                  </div>
                  <div className="text-sm text-[#6B6E66]">
                    plays {suggestion.longer.effective_yards}y
                  </div>
                </div>
              </div>
              {breakdownLabel && (
                <div className="text-[10px] text-[#6B6E66] mt-1">{breakdownLabel}</div>
              )}
            </div>
          )}
          {suggestion.mode === 'forced' && (() => {
            // Two flavours of "forced":
            //   1. target > longest club  → just show the longest club, no scolding.
            //      A par-5 tee shot at 450y is not "out of range", it's a tee shot.
            //   2. target < shortest club → suggest the shortest + "choke down".
            const sortedByDist = [...(suggestion.pick ? clubs : [])].sort(
              (a, b) => Number(b.distance_yards) - Number(a.distance_yards)
            );
            const isLongest = sortedByDist[0]?.name === suggestion.pick.name;
            const isShortest = sortedByDist[sortedByDist.length - 1]?.name === suggestion.pick.name;
            return (
              <div>
                <span className="text-3xl font-bold text-[#1B3C35]" style={{ fontFamily: 'Outfit' }}>
                  {suggestion.pick.name}
                </span>
                <div className="text-sm text-[#6B6E66] mt-0.5">
                  {isLongest && yards > suggestion.pick.effective_yards
                    ? `plays ${suggestion.pick.effective_yards}y`
                    : isShortest && yards < suggestion.pick.effective_yards
                      ? `choke down · plays ${suggestion.pick.effective_yards}y full`
                      : `plays ${suggestion.pick.effective_yards}y`}
                </div>
                {breakdownLabel && (
                  <div className="text-[10px] text-[#6B6E66] mt-0.5">{breakdownLabel}</div>
                )}
              </div>
            );
          })()}
          {hazardStrategy && (
            <div className="text-[11px] text-[#1B3C35] font-semibold mt-1.5 pt-1.5 border-t border-[#C96A52]/15"
              data-testid={`caddie-strategy-${hole.hole}`}>
              🎯 {hazardStrategy}
            </div>
          )}
        </div>
      )}
      {!suggestion && clubs.length === 0 && (
        <a href="/my-bag" className="text-xs text-[#C96A52] hover:underline block text-center py-1"
          data-testid={`setup-bag-link-${hole.hole}`}>
          Set up your bag to see Caddie suggestions →
        </a>
      )}
    </div>
  );
}
